import { Component, inject, OnDestroy, OnInit, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NetworkStatusService } from './core/services/network-status/network-status.service';
import { OfflineComponent } from "./shared/components/offline/offline.component";
import { NotificationComponent } from "./shared/components/notification/notification.component";
import { LoaderComponent } from "./shared/components/loader/loader.component";
import { ThemeService } from './core/services/theme/theme.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, OfflineComponent, NotificationComponent, LoaderComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:[NetworkStatusService]
})
export class App implements OnInit, OnDestroy {
  private network = inject(NetworkStatusService);
  private readonly document = inject(DOCUMENT);
  private readonly destroy$ = new Subject<void>();

  protected isOnline = this.network.isOnline;

  private mediaQueryList: MediaQueryList | null = null;

  private readonly theme = inject(ThemeService);

  private readonly onSystemThemeChange = (event: MediaQueryListEvent) => {
    this.theme.followSystemTheme(event.matches);
  }

  ngOnInit(): void {
    this.initializeFavicon();
    
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

    this.theme.followSystemTheme(this.mediaQueryList.matches);

    this.mediaQueryList.addEventListener('change', this.onSystemThemeChange);

    // Note: Theme tokens are now loaded from API via APP_INITIALIZER
    // Theme colors (primary, accent) and mode are automatically applied during app initialization
    // This ensures the theme is ready before the UI renders, preventing flickering
  }

  ngOnDestroy(): void {
    if (!this.mediaQueryList) {
      return;
    }

    if (typeof this.mediaQueryList.removeEventListener === 'function') {
      this.mediaQueryList.removeEventListener('change', this.onSystemThemeChange);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFavicon(): void {
    // Favicon is already set by APP_INITIALIZER in theme-initializer
    // This method is kept for compatibility in case favicon needs to be updated later
    const faviconLink = this.document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    
    if (!faviconLink) {
      // Create favicon link if it doesn't exist
      const link = this.document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      this.document.head.appendChild(link);
    }
  }
}
